<script setup>
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';
import { getAllOperatorsConfig } from '~/bi/utils/bi-filter-helper.utils';
import { useCommonImports } from '~/common/composables/common-imports.composable';

// --------------------------------- Imports -------------------------------- //

// ---------------------------------- Props --------------------------------- //

// ---------------------------------- Emits --------------------------------- //

// ---------------------------- Injects/Provides ---------------------------- //

// ----------------------- Variables - Pinia - consts ----------------------- //
const {
  $t,
  $services,
} = useCommonImports();

const bi_store = useBiStore();
const { constructFieldName } = useBIQueryBuilder();

// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //
const all_operators = getAllOperatorsConfig();
const date_bins = [{ label: $t('Day'), value: 'day' }, { label: $t('Week'), value: 'week' }, { label: $t('Month'), value: 'month' }, { label: $t('Quarter'), value: 'quarter' }, { label: $t('Year'), value: 'year' }];

// ------------------------ Variables - Local - refs ------------------------ //
const selected_bin = ref(null);
// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //
const global_filter_list = computed(() => bi_store.selected_dashboard_details?.meta?.global_filters || []);

// -------------------------------- Functions ------------------------------- //
function getValidType(type) {
  if (type === 'text')
    return 'text';
  if (type === 'number')
    return 'integer';
  if (['date_range', 'date_interval'].includes(type))
    return 'date';
  return 'text';
}

function onFilterApply(filter_uid, filter_payload) {
  bi_store.updateAppliedGlobalFilter(filter_uid, filter_payload);
}

function onFilterClear(filter_uid) {
  bi_store.removeAppliedGlobalFilter(filter_uid);
}

function getFilterText(filter) {
  const applied_filter_config = bi_store.get_applied_filters_for_dashboard[filter.uid];
  if ([
    'in',
    'not_in',
    'contains_any_of',
    'contains_all_of',
    'contains_exactly',
    'contains_none_of',
  ].includes(applied_filter_config?.op) && applied_filter_config?.value.length) {
    return `${filter.general.filter_name} (${applied_filter_config.value.length})`;
  }

  if (filter.general.filter_type === 'date_interval' && bi_store.get_applied_filters_for_dashboard[filter.uid]?.value) {
    const date_bin_object = date_bins.find(bin => bin.value === bi_store.get_applied_filters_for_dashboard[filter.uid].value);
    return `${filter.general.filter_name}: ${date_bin_object.label}`;
  }

  return filter.general.filter_name;
}

function getFilterConfig(filter) {
  const applied_global_filter = bi_store.get_applied_filters_for_dashboard[filter.uid];
  if (applied_global_filter) {
    return {
      ...applied_global_filter.meta.filter_config,
      operator: all_operators[bi_store.get_applied_filters_for_dashboard[filter.uid].op],
    };
  }

  return {};
}

function onDateBinApply(filter_uid) {
  if (bi_store.get_applied_filters_for_dashboard[filter_uid]?.value && bi_store.get_applied_filters_for_dashboard[filter_uid].value === selected_bin.value?.value) {
    bi_store.removeAppliedGlobalFilter(filter_uid);
    return;
  }

  const filter_payload = {
    field: null,
    op: null,
    value: selected_bin.value.value,
    meta: {
      is_stage_field: false,
      filter_config: {
        operator_value: selected_bin.value.value,
      },
    },
  };

  onFilterApply(filter_uid, filter_payload);
}

function onSelectBin(bin, filter, close = () => {}) {
  selected_bin.value = bin;
  onDateBinApply(filter.uid);
  close?.();
}

function getValuesFromExistingFilters(params) {
  const datasets = bi_store.get_global_filters_with_applied_filters
    .filter(filter => filter.general.dataset === params.columnConfig.table_name)
    .map(filter => filter.general.dataset);

  const field = constructFieldName({ label: params.columnConfig.name, table_name: params.columnConfig.table_name });
  const filters = bi_store.get_global_filters_with_applied_filters.filter(filter => filter.general.column && filter.general.column !== field).map((filter) => {
    return {
      ...filter.applied_filter_config,
      field: filter.general.column,
    };
  });
  async function getValues(query) {
    const payload = { columns: [{
      alias: 'value',
      field,
      unnest: params.columnConfig.type === 'array',
    }, {
      alias: 'count',
      field,
      agg: 'count',
    }], table: params.columnConfig.table_name, filters, limit: 1000 };

    if (query.search) {
      filters.push({
        field: params.columnConfig.type === 'array' ? 'value' : field,
        op: '__icontains',
        value: query.search,
      });
    }

    const response = await $services.bi_query.execute({
      id: bi_store.connection_id,
      body: payload,
    });
    const { data } = response.data;
    return data.map(item => item.value);
  }

  return {
    has_filters: datasets.length === 1 && filters.length > 0,
    getValues,
  };
}

// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //
</script>

<template>
  <div v-for="filter in global_filter_list" :key="filter.uid">
    <HawkMenu
      class="flex items-center justify-between gap-2 cursor-pointer group hover:bg-gray-50 rounded-lg"
      additional_trigger_classes="!ring-0 !focus:ring-0 !w-full"
      :items="[]"
      position="fixed"
    >
      <template #trigger="{ open }">
        <div
          class="flex items-center justify-between px-3 py-1 gap-2 border border-gray-300 rounded-lg bg-white"
          :class="{ 'border-primary-600 !bg-primary-50': bi_store.get_applied_filters_for_dashboard[filter.uid] }"
        >
          <div class="flex items-center">
            <HawkText
              :length="15"
              class="text-gray-700 text-sm font-medium bi-exportable-text"
              :class="{ 'text-primary-700': bi_store.get_applied_filters_for_dashboard[filter.uid] }"
              :content="getFilterText(filter)"
            />
          </div>
          <div :class="{ 'text-primary-700': bi_store.get_applied_filters_for_dashboard[filter.uid] }">
            <IconHawkChevronUp v-if="open" />
            <IconHawkChevronDown v-else />
          </div>
        </div>
      </template>
      <template #content="{ close: closeOperatorMenu }">
        <div class="bg-white p-1 rounded-lg w-[350px]" :class="{ '!w-[150px]': filter.general.filter_type === 'date_interval' }">
          <div v-if="filter.general.filter_type === 'date_interval'">
            <div
              v-for="bin in date_bins" :key="bin.value"
              class="p-2 text-sm font-medium rounded-lg cursor-pointer text-gray-700 hover:bg-gray-50 flex items-center my-1"
              @click="onSelectBin(bin, filter, closeOperatorMenu)"
            >
              {{ bin.label }}
              <IconHawkCheck v-if="bin.value === bi_store.get_applied_filters_for_dashboard[filter.uid]?.value" class="ml-auto text-primary-500 w-5 h-5 min-w-5" />
            </div>
          </div>
          <BiFilter
            v-else
            :selected-column-config="{
              type: getValidType(filter.general.filter_type),
              ...(filter.general.column && filter.general.column_name ? {
                single_select: filter.general.set_single_select_value,
                table_name: filter.general.dataset,
                name: filter.general.column_name,
                is_table_field: true,
              } : {}),
            }"
            :filter-config="getFilterConfig(filter)"
            :get-values-from-existing-filters="getValuesFromExistingFilters"
            show-clear-button
            @cancel="closeOperatorMenu"
            @apply="(filter_payload) => { onFilterApply(filter.uid, filter_payload); closeOperatorMenu(); }"
            @clear="() => { onFilterClear(filter.uid); closeOperatorMenu(); }"
          />
          <!-- {{ filter.general.set_single_select_value }} -->
        </div>
      </template>
    </HawkMenu>
  </div>
</template>
